import React, { useEffect, useRef, useState } from "react";
import { Box, Typography, Grid, Container, List, ListItem, ListItemText, Paper } from "@mui/material";
import Menu from "../Menu";

const IEPFClaim: React.FC = () => {
  const contentContainerRef = useRef<HTMLDivElement | null>(null);
  const [isSticky, setIsSticky] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (contentContainerRef.current) {
        const contentRect = contentContainerRef.current.getBoundingClientRect();
        setIsSticky(contentRect.top <= 0);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <div ref={contentContainerRef}>
      {/* Static Banner Section */}
      <Box
        sx={{
          width: "100%",
          height: "50vh",
          position: "relative",
          overflow: "hidden",
          background: `
            linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.7)),
            url(${"/assets/service_01.jpg"})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <Menu isSticky={isSticky} />
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            color: "#fff",
            textAlign: "center",
          }}
        >
          <Typography variant="h3" fontWeight="bold" letterSpacing={1}>
            Claim from IEPF
          </Typography>
        </Box>
      </Box>
  
      {/* Sections Below */}
      <Box sx={{ padding: "20px", backgroundColor: "#f9f9f9", color: "#000" }}>
        <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
  
          {/* Section 1 */}
          <Box sx={{ mb: 6 }}>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              What is IEPF and Why Are Shares Transferred to IEPF?
            </Typography>
  
            <Typography variant="body1" paragraph>
              The Investor Education and Protection Fund (IEPF) is a fund established by the Central Government of India under the provisions of the Companies Act, 2013. The IEPF promotes investor awareness and protects the interests of investors.
            </Typography>
  
            <Typography variant="body1" paragraph>
              In simple terms, equity shares are transferred to the IEPF when a shareholder fails to claim dividends for seven consecutive years or more.
            </Typography>
  
            <Typography variant="body1" paragraph>
              Every company must retain unclaimed dividends, deposits, share application money, and debentures in separate bank accounts for seven years. If still unclaimed after that period, the company must transfer them to the IEPF.
            </Typography>
  
            <Typography variant="body1" paragraph>
              As per Section 124 of the Companies Act, 2013:
            </Typography>
  
            <ul style={{ paddingLeft: "20px", lineHeight: "1.8" }}>
              <li><Typography variant="body1"><b>The amount</b> remaining unpaid or unclaimed in the unpaid dividend account for seven consecutive years must be transferred along with accrued interest to the IEPF.</Typography></li>
              <li><Typography variant="body1"><b>Redemption amounts</b> of preference shares remaining unpaid or unclaimed for seven or more years must also be transferred.</Typography></li>
              <li><Typography variant="body1"><b>All shares</b> for which dividends are unclaimed or unpaid for seven consecutive years must be transferred to the IEPF by the company.</Typography></li>
            </ul>
  
            <Typography variant="body1" paragraph>
              Additionally, Section 125(3)(a) provides that shareholders can claim a refund of unpaid dividends, matured deposits, or matured debentures transferred to the IEPF.
            </Typography>
          </Box>
  
          {/* Section 2 */}
          <Box sx={{ mb: 6 }}>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              How to Avoid Shares Being Transferred to IEPF?
            </Typography>
  
            <Typography variant="body1" paragraph>
              To prevent shares from being transferred to the IEPF, investors should regularly claim their dividends within the stipulated time frame provided by the company.
            </Typography>
          </Box>
  
          {/* Section 3 */}
          <Box sx={{ mb: 6 }}>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              How to Claim Shares/Dividends from IEPF?
            </Typography>
  
            <Typography variant="body1" paragraph>
              Given that claims remain unattended for seven years, the Government has laid down elaborate and strict procedures to ensure that only rightful owners can reclaim their assets.
            </Typography>
  
            <Typography variant="body1" paragraph>
              To claim shares/dividends transferred to the IEPF, investors must follow this process:
            </Typography>
  
            <ul style={{ paddingLeft: "20px", lineHeight: "1.8" }}>
              <li>
                <Typography variant="body1">
                  Complete the details in the IEPF-5 Form and file it online on the MCA Portal. If the claim is made by a legal heir or nominee due to the death of the original shareholder, additional documents such as the original death certificate, notarized affidavit, indemnity bond, will/succession certificate, and other required documents must be provided.
                </Typography>
              </li>
              <li>
                <Typography variant="body1">
                  Print the submitted IEPF-5 Form, which will include the Service Request Number (SRN) as proof of submission to the MCA.
                </Typography>
              </li>
              <li>
                <Typography variant="body1">
                  Submit the original indemnity bond, printed IEPF-5 form, and supporting documents to the Nodal Officer (IEPF) of the company at its registered office.
                </Typography>
              </li>
            </ul>
  
            <Typography variant="body1" paragraph sx={{ mt: 2 }}>
              Upon receiving the completed claim forms, the company will scrutinize the documents. After verifying the accuracy of the claim, the company will submit a verification report to the IEPF Authority.
            </Typography>
  
            <Typography variant="body1" paragraph>
              The IEPF Authority, based on the verification report of the company, will process the request and refund the dividend or transfer the equity shares electronically to the claimant's bank account or demat account.
            </Typography>
  
            <Typography variant="body1" paragraph>
              In conclusion, the process of claiming refunds involves several administrative steps and requires precise knowledge of the procedure. Therefore, it is advisable to seek professional assistance to avoid claim rejections and to ensure a faster and smoother recovery.
            </Typography>
          </Box>
  
        </Container>
      </Box>
    </div>
  );
};

export default IEPFClaim;
